import { type FormEvent, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useConfig } from 'shared/config/ConfigContext';
import { ppAutocompleteMapper } from 'shared/search/common/autocomplete/utils/autocompleteMapper';
import { generateGooglePlacesSessionToken } from 'shared/search/common/utils/googlePlacesSessionToken';
import { popularDestinations, popularDestinationsTip, popularDestinationsTitle } from 'shared/search/prepackage/data/popularDestinations';
import { PrepackageSearchForm } from 'shared/search/prepackage/PrepackageSearchForm';
import { ppDefaultState } from 'shared/search/prepackage/prepackageDefaultState';
import {
    appendPrepackageAutocompleteVariant,
    getPrepackageAutocompleteVariant,
    PREPACKAGE_SMART_FILL_EXPERIMENT,
} from 'shared/search/prepackage/utils/geoAutocompleteVariant';
import { trackPopularDestinationClick } from 'shared/search/prepackage/utils/tracking';
import type { AutocompleteOption, MonthDuration, MonthPickerMonth, PrepackageSearchState } from 'shared/search/types';
import { safeFormatISODate, safeParseISO } from 'shared/util/datetime-fns';
import { useExperiment } from '@/hooks/experiments';
import type { PaleState, ThunkDispatchFunc } from '@/interfaces';
import { filtersToApiQueryParams } from '@/lib/utils/queryParamFilterConversions';
import { stringifyParams } from '@/lib/utils/queryString';
import { actions } from '@/state/filterActions';
import { fetchResults } from '@/state/resultActions';

type SearchFormProps = {
    onSubmit: () => void;
};

export const SearchForm = ({ onSubmit }: SearchFormProps) => {
    const dispatch = useDispatch<ThunkDispatchFunc>();
    const { apiUrlPP } = useConfig();
    const experiment = useExperiment();
    const filters = useSelector((state: PaleState) => state.filters);
    const { travelMonths, duration, origin, numberOfAdults, agesOfChildren, departureDate, returnDate, destinationLabel } = filters;
    const [searchType, setSearchType] = useState<PrepackageSearchState['searchType']>(
        travelMonths?.length !== 0 || !!duration ? 'flexible' : 'fixed',
    );
    const [googlePlacesSessionToken] = useState(generateGooglePlacesSessionToken);

    const autocompleteParams = stringifyParams(
        filtersToApiQueryParams({
            numberOfAdults: numberOfAdults ?? 2,
            agesOfChildren: agesOfChildren ?? [],
            origin,
            departureDate,
            returnDate,
            travelMonths: travelMonths ?? [],
            duration,
            ignoredFilters: searchType === 'fixed' ? ['duration', 'travelMonths'] : ['departureDate', 'returnDate'],
        }),
    );
    const autocompleteVariant = getPrepackageAutocompleteVariant(experiment?.[PREPACKAGE_SMART_FILL_EXPERIMENT]);
    const autocompleteUrl = appendPrepackageAutocompleteVariant(
        `${apiUrlPP}/geo-autocomplete?${autocompleteParams}&query=`,
        autocompleteVariant,
    );

    const handleDestinationSelect = useCallback(
        (option: AutocompleteOption | null, typedText?: string) => {
            if (!option && typedText) {
                dispatch(actions.setDestinationLabel(typedText));
            } else if (option?.bounds) {
                if (option.id.startsWith('popular-')) {
                    trackPopularDestinationClick(option.text);
                }
                dispatch(actions.setDestinationLabel(option.text));
                dispatch(actions.setDestinationCountry(option.isCountry ? option.country : undefined));
                dispatch(
                    actions.setBoundingBox({
                        minLat: option.bounds.southwest_lat,
                        minLon: option.bounds.southwest_lng,
                        maxLat: option.bounds.northeast_lat,
                        maxLon: option.bounds.northeast_lng,
                    }),
                );
            } else {
                dispatch(actions.clearGeoFilters());
            }
        },
        [dispatch],
    );

    const state: PrepackageSearchState = {
        flexibleDeparture: departureDate?.flexibility ?? ppDefaultState.flexibleDeparture,
        departureDate: safeParseISO(departureDate?.date, null),
        returnDate: safeParseISO(returnDate?.date, null),
        searchType,
        travelMonths: travelMonths ?? [],
        duration: { from: duration?.[0] ?? ppDefaultState.duration.from, to: duration?.[1] ?? ppDefaultState.duration.to },
        originAirport: origin ?? 'OSL',
        numberOfAdults: numberOfAdults ?? ppDefaultState.numberOfAdults,
        agesOfChildren: agesOfChildren ?? ppDefaultState.agesOfChildren,
        destination: destinationLabel,
    };

    return (
        <PrepackageSearchForm
            ready={true}
            state={state}
            invalid={{}}
            validationErrors={{}}
            autoCompleteData={{
                ppAutocompleteUrl: autocompleteUrl,
                autocompleteMapper: ppAutocompleteMapper,
                onDestinationSelect: handleDestinationSelect,
                headers: { 'x-gtoken': googlePlacesSessionToken },
                initialOptions: popularDestinations,
                initialOptionsTitle: popularDestinationsTitle,
                tip: popularDestinationsTip,
            }}
            onSubmit={(event: FormEvent<HTMLFormElement>): void => {
                event.preventDefault();
                dispatch(fetchResults());
                onSubmit();
            }}
            onSearchTypeChange={(searchType: PrepackageSearchState['searchType']): void => {
                setSearchType(searchType);
                if (searchType === 'fixed') {
                    dispatch(actions.setIgnoredFilters(['duration', 'travelMonths']));
                    dispatch(actions.resetTravelMonths());
                    dispatch(actions.changeDuration(undefined));
                } else {
                    dispatch(actions.setIgnoredFilters(['departureDate', 'returnDate']));
                }
            }}
            onOriginAirportChange={(originAirport: string): void => {
                dispatch(actions.changeOrigin(originAirport));
            }}
            onToggleTravelMonth={(month: MonthPickerMonth): void => {
                dispatch(actions.toggleTravelMonth(month));
            }}
            onDurationChange={(duration: MonthDuration): void => {
                dispatch(actions.changeDuration([duration.from, duration.to]));
            }}
            onDatesChange={(dates: { startDate: Date | null; endDate: Date | null }): void => {
                dispatch(actions.setDates({ startDate: safeFormatISODate(dates.startDate), endDate: safeFormatISODate(dates.endDate) }));
            }}
            onPaxChange={(adults: number, agesOfChildren: number[]): void => {
                dispatch(actions.changePaxCombination({ numberOfAdults: adults, agesOfChildren: agesOfChildren }));
            }}
            compactTop={true}
        />
    );
};
